import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import i18n from '@app/app/i18n/config';
import { replaceLanguagePrefix, urlLangToLocale } from '@app/app/i18n/utils/language-url';
import { SUPPORTED_LANGUAGE_CODES } from '@app/app/i18n/constants';
import { setLanguageCookie } from '@app/app/i18n/utils/language-cookie';
import { isSpecialPath } from '@app/utils/route_path';

/**
 * Компонент для синхронизации языка i18n с языковым префиксом в URL
 * Теперь у нас есть два маршрута для каждого пути: один без префикса, один с префиксом
 * Поэтому нормализация пути не нужна - только синхронизация языка
 */
export const LanguageSyncWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Синхронизация языка из параметра URL в i18n
  useLayoutEffect(() => {
    const urlLang = params.lang;

    if (urlLang) {
      // Нормализуем язык из URL формата (pt-br) в формат локали (pt-BR)
      const localeLang = urlLangToLocale(urlLang);

      if (SUPPORTED_LANGUAGE_CODES.includes(localeLang)) {
        // Обновляем язык в i18n, если он отличается
        // Сначала обновляем куку, потом i18n (для консистентности с LanguageSwitcher)
        if (localeLang !== i18n.language) {
          setLanguageCookie(localeLang, true);
          i18n.changeLanguage(localeLang);
        }
      }
    } else if (!urlLang && i18n.language !== 'en') {
      // Если в URL нет языкового префикса, но язык не английский
      // Нужно добавить префикс в URL для синхронизации
      // НО: не добавляем префикс для специальных путей (sharing, admin)
      if (!isSpecialPath(location.pathname)) {
        const newPath = replaceLanguagePrefix(location.pathname, i18n.language);
        if (newPath !== location.pathname) {
          // Сохраняем query параметры при редиректе
          const newUrl = `${newPath}${location.search}`;
          navigate(newUrl, { replace: true });
        }
      }
    }
  }, [params.lang, location.pathname, location.search, navigate]);

  // Синхронизация языка из i18n в URL при изменении языка
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      // Игнорируем изменения, которые произошли из-за изменения URL
      // Нормализуем params.lang из URL формата для сравнения
      const normalizedUrlLang = params.lang ? urlLangToLocale(params.lang) : null;
      if (normalizedUrlLang === lng) {
        return;
      }

      // Обновляем URL с новым языковым префиксом
      // НО: не обновляем URL для специальных путей (sharing, admin)
      if (!isSpecialPath(location.pathname)) {
        const newPath = replaceLanguagePrefix(location.pathname, lng);

        if (newPath !== location.pathname) {
          // Сохраняем query параметры при редиректе
          const newUrl = `${newPath}${location.search}`;
          navigate(newUrl, { replace: true });
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [location.pathname, location.search, navigate, params.lang]);

  return <>{children}</>;
};
